import type { ComponentType } from 'react'
import type { LucideIcon } from 'lucide-react'

export type UseCaseId =
  | 'adaptation-planning'
  | 'land-use-planning-and-development'
  | 'emergency-planning-and-business-continuity'
  | 'asset-and-infrastructure-planning'

export type AccentColor = 'deepTeal' | 'sageGreen' | 'dustyRose' | 'softCream'

export type UseCaseIconComponent = ComponentType<{
  size?: number
}>

export interface TutorialItem {
  type: 'TUTORIAL' | 'DEMO'
  date: string
  title: string
  description: string
  thumbnail: string
  href: string
}

export interface ArticleItem {
  title: string
  description: string
  href: string
}

export interface ArticleGroup {
  title: string
  articles: ArticleItem[]
}

export interface CapabilityLevel {
  level: 1 | 2 | 3 | 4
  title: string
  description: string
}

export type DatasetCategory =
  | 'Hazard'
  | 'Infrastructure'
  | 'Exposure'
  | 'Vulnerability'
  | 'Engineered elements'
  | 'Spatial value layers'
  | 'Inputs used to estimate consequences'
  | 'Planning and decision inputs'
  | 'Infrastructure and assets'
  | 'Services and response'
  | 'Population and demand'
  | 'Asset identification'
  | 'Asset attributes'
  | 'Consequence and criticality'
  | 'System and network'
  | 'Strategy and scenario testing'
  | 'Spatial'
  | 'Consequence and value'
  | 'Planning and scenarios'
  | 'Contextual and constraint layers'

export interface DataRequirementRow {
  dataset: string
  category: DatasetCategory
  startLevel: 1 | 2 | 3 | 4
}

export interface UseCaseConfig {
  id: UseCaseId
  title: string
  tagline: string
  accentColor: AccentColor
  iconComponent: UseCaseIconComponent
  // Overview
  forumUrl: string
  overviewBody: string
  framingQuestion: string
  roles: string[]
  keyQuestions: string[]
  relatedUseCaseIds: UseCaseId[]

  // Learning Pathway
  learningPathwayIntro: string
  tutorials: TutorialItem[]
  articleGroups: ArticleGroup[]

  // Data Needs
  dataNeedsIntro: string
  capabilityLevels: CapabilityLevel[]
  dataRequirements: DataRequirementRow[]
}

export const accentBgMap: Record<AccentColor, string> = {
  deepTeal: 'bg-deepTeal',
  sageGreen: 'bg-sageGreen',
  dustyRose: 'bg-dustyRose',
  softCream: 'bg-softCream',
}

export const accentTextMap: Record<AccentColor, string> = {
  deepTeal: 'text-deepTeal-700',
  sageGreen: 'text-sageGreen-700',
  dustyRose: 'text-dustyRose-600',
  softCream: 'text-softCream-600',
}

export const accentBorderMap: Record<AccentColor, string> = {
  deepTeal: 'border-t-deepTeal',
  sageGreen: 'border-t-sageGreen',
  dustyRose: 'border-t-dustyRose',
  softCream: 'border-t-softCream',
}

export const accentLevelBgMap: Record<AccentColor, string[]> = {
  deepTeal: ['bg-deepTeal-100', 'bg-deepTeal-200', 'bg-deepTeal-300', 'bg-deepTeal-400'],
  sageGreen: ['bg-sageGreen-100', 'bg-sageGreen-200', 'bg-sageGreen-300', 'bg-sageGreen-400'],
  dustyRose: ['bg-dustyRose-100', 'bg-dustyRose-200', 'bg-dustyRose-300', 'bg-dustyRose-400'],
  softCream: ['bg-softCream-100', 'bg-softCream-200', 'bg-softCream-300', 'bg-softCream-400'],
}

export interface ComingUpFeatureItem {
  type: 'NEW FEATURE'
  title: string
  description: string
  href: string
}

export type RecentlyReleasedType = 'NEW FEATURE' | 'TECH UPDATE' | 'BETA RELEASE'
export type RecentlyReleasedAction =
  | 'tutorial'
  | 'release-notes'
  | 'learn-more'
  | 'feedback'
  | 'none'

export interface RecentlyReleasedItem {
  type: RecentlyReleasedType
  title: string
  description: string
  action: RecentlyReleasedAction
  href: string
}

export interface AdaptationCycleBaseConfig {
  id: string
  title: string
  description: string
  tagline: string
  heroImage: string
}

export interface AdaptationCycleStageItem {
  stage: number
  title: string
  description: string
  link: string
}

export interface AdaptationCycleMainStageItem {
  title: string
  description: string
  icon: LucideIcon
}

export interface AdaptationCycleOverviewConfig extends AdaptationCycleBaseConfig {
  stagesIntro: {
    title: string
    description: string
  }
  stages: AdaptationCycleStageItem[]
  startHere: {
    label: string
    anchor: string
  }
  jurisdictionNote: string
  forumCallout: {
    title: string
    description: string
    actionLabel: string
    href: string
  }
  mainStages: {
    title: string
    description: string
    stages: AdaptationCycleMainStageItem[]
    footnote: string
  }
  aboutGuidance: {
    title: string
    paragraphs: string[]
  }
}

export interface AdaptationCycleActivityStream {
  icon: LucideIcon
  title: string
  points: string[]
}

export interface AdaptationCycleOutputItem {
  text: string
  link?: {
    text: string
    href: string
  }
}

export interface AdaptationCycleResourceItem {
  title: string
  description: string
  href: string
}

export interface AdaptationCycleResourceGroup {
  title: string
  articles: AdaptationCycleResourceItem[]
}

export interface AdaptationCycleMfeAlignment {
  step: number
  label: string
}

export interface AdaptationCycleRelatedActivity {
  stageId: string
  activity?: string
  label: string
}

export interface AdaptationCycleActivity {
  activity: string
  title: string
  // Slug of the activity's real Forum thread (the part after ".../examples-").
  // Static and independent of `title` — the Forum thread name doesn't follow title edits.
  forumSlug: string
  mfeAlignment: AdaptationCycleMfeAlignment[]
  description: string
  streams: AdaptationCycleActivityStream[]
  outputs: AdaptationCycleOutputItem[]
  connections: string[]
  relatedActivities: AdaptationCycleRelatedActivity[]
  moveOnWhen: string[]
  resources: AdaptationCycleResourceGroup[]
}

export interface AdaptationCycleStageConfig extends AdaptationCycleBaseConfig {
  outcome: {
    title: string
    description: string
  }
  topParagraphs: string[]
  activities: AdaptationCycleActivity[]
  previousStage: string | null
  nextStage: string
}
